<template>
  <div class="dashboard-base">
    <slot name="top" />

    <!-- CAT New Project Button -->
    <div
      v-if="submitableModules.includes(enums.modules.CAT)"
      class="row justify-end items-center q-mb-md"
    >
      <div class="col-auto">
        <re-button
          size="md"
          tag="router-link"
          :to="{ name: 'catalog-start' }"
          color="accent"
          :label="t('dashboard.new_project.title')"
        />
      </div>
    </div>

    <!-- Valuers Workflow -->
    <q-expansion-item
      v-if="showValuersWorkload"
      :label="t('monolith.dashboard.valuers_workload')"
      header-class="text-primary text-h5"
      class="q-my-xl shadow-2"
    >
      <valuer-workload class="q-pa-md bg-white" />
    </q-expansion-item>

    <!-- Filters Row -->
    <div class="q-mb-md re-table-filters row justify-between items-center q-col-gutter-md">
      <!-- Time Filter -->
      <re-multi-choice
        v-if="showTimeFilter"
        v-model="selectedTimeFilter"
        :label="null"
        :disable="!!customerRefFilter || !!valReqsFilter"
        :options="timeFilterOptions"
        size="md"
        :color="valReqsFilter ? enums.feedbackType.WARNING : undefined"
        clearable
        @update:model-value="handleTimeFilterChange"
      />

      <!-- My/All Requests Toggle  -->
      <re-multi-choice
        v-if="isAllRequestsTabEnabled && showRequestsFilter"
        v-model="isUserRequests"
        aria-label="requests"
        :label="null"
        :options="[
          { label: t(`monolith.dashboard.my_requests`), value: true },
          {
            label: t(`monolith.dashboard.all_requests`),
            value: false,
          },
        ]"
        :color="valReqsFilter ? enums.feedbackType.WARNING : undefined"
        size="md"
        class="col-xs-12 col-sm-grow"
        @update:model-value="
          (val: boolean) => {
            router.replace({ query: { ...route.query, isUserRequests: String(val) } })
          }
        "
      />

      <!-- Customer Ref Field -->
      <re-input
        v-if="isCustomerRefFieldEnabled && showCustomerRefFilter"
        v-model="tempCustomerRefFilter"
        :placeholder="t('monolith.shared.customer_ref')"
        size="md"
        self-start
      >
        <template #embed>
          <re-button
            icon="mdi-arrow-right"
            type="submit"
            color="main"
            stretch
            :disable="tempCustomerRefFilter == customerRefFilter"
            :loading="isBusy"
            size="md"
            @click="
              () => {
                isUserRequests = tempCustomerRefFilter === ''
                customerRefFilter = tempCustomerRefFilter
              }
            "
          />
        </template>
      </re-input>

      <!-- Module Filter -->
      <q-option-group
        v-if="isModuleFilterEnabled && showModuleFilter"
        v-model="moduleFilter"
        class="row"
        :options="valuationTypeOptions.filter((item) => filteredAccessibleModules.includes(item.value))"
        type="checkbox"
      />

      <div class="gt-sm col" />

      <!-- Expand/Collapse Button -->
      <re-button
        v-if="isExpandable && q.screen.gt.sm"
        :label="allRowsExpanded ? t('monolith.dashboard.collapse_all') : t('monolith.dashboard.expand_all')"
        icon-right="mdi-unfold-more-horizontal"
        size="md"
        outlined
        @click="toggleExpandAll"
      />

      <!-- Filters Button -->
      <re-button
        v-if="showSidebarFilters"
        :label="t('dashboard.filters.title')"
        icon-right="mdi-filter-variant"
        size="md"
        outlined
        @click="showFilters = !showFilters"
      >
        <q-badge
          v-if="filtersCount"
          floating
        >
          {{ filtersCount }}
        </q-badge>
      </re-button>
    </div>

    <!-- Active Filters Chips -->
    <div
      v-if="showSidebarFilters"
      class="row q-gutter-md q-mb-md"
    >
      <q-chip
        v-for="filter in activeFilters"
        :key="filter.key"
        clickable
        icon="mdi-close"
        @click="clearFilter(filter.key)"
      >
        {{ t(filter.label) }}
      </q-chip>
    </div>

    <!-- Dashboard Table -->
    <dashboard-table
      v-model:col-filters="colFilters"
      v-model:expanded="expandedRows"
      :show-filters="false"
      :show-header-filters="showHeaderFilters && !q.screen.xs"
      :table-data="getFilteredData"
      :loading="isBusy"
      row-key="valuation_request_ref"
      v-bind="$attrs"
      class="sticky-first-column request-table"
      :class="{ 'request-table--no-border': q.screen.lt.md }"
      :no-data-label="t('monolith.shared.no_requests')"
      :no-results-label="t('monolith.shared.no_results')"
      :is-expandable="isExpandable"
      @refetch="() => refetch()"
    >
      <!-- Filter Banner -->
      <template
        v-if="customerRefFilter || valReqsFilter"
        #table-top
      >
        <re-banner
          color="warning"
          class="full-width"
        >
          <span v-if="customerRefFilter && valReqsFilter">
            Displaying results filtered by valuation request and customer references.
          </span>
          <span v-else-if="customerRefFilter">Displaying results filtered by customer references.</span>
          <span v-else>Displaying results filtered by valuation request references.</span>
          <template #action>
            <re-button
              label="Clear filter"
              size="md"
              class="q-ml-md"
              :to="{ query: {} }"
              @click="clearFilters"
            />
          </template>
        </re-banner>
      </template>
    </dashboard-table>

    <!-- Filters Sidebar -->
    <DashboardFilters
      v-if="showSidebarFilters && dashboardConfig"
      v-model:open="showFilters"
      v-model:col-filters="colFilters"
      :config="dashboardConfig"
    />
  </div>
</template>

<script setup lang="ts">
import { useRouteQuery } from '@vueuse/router'
import { useQuasar } from 'quasar'
import { computed, inject, onMounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { onBeforeRouteUpdate, RouteLocationNormalized, useRoute, useRouter } from 'vue-router'

import ValuerWorkload from '@/apps/MainApp/views/Dashboard/ValuerWorkload.vue'
import { useOptionsFormatter } from '@/composables/optionsFormatter'
import { useConfig } from '@/composables/useConfig'
import { useCurUser } from '@/composables/useCurUser'
import { getTimeFilterOptions } from '@/composables/useRequestList'
import { dashboardInstallerKey, requestListKey } from '@/keys'
import enums from '@/plugins/enums.js'
import utils from '@/plugins/utils.js'
import { WrappedRequest } from '@/types/curRequest'

import { useBaseDashboard } from './core/builder'
import DashboardFilters from './DashboardFilters.vue'
import DashboardTable from './DashboardTable.vue'

// Props
const props = withDefaults(
  defineProps<{
    isExpandable?: boolean
    showSidebarFilters?: boolean
    showHeaderFilters?: boolean
    showCustomerRefFilter?: boolean
    showValuerWorkload?: boolean
    showTimeFilter?: boolean
    showRequestsFilter?: boolean
    showModuleFilter?: boolean
  }>(),
  {
    isExpandable: false,
    showSidebarFilters: true,
    showHeaderFilters: true,
    showCustomerRefFilter: false,
    showValuerWorkload: false,
    showTimeFilter: true,
    showRequestsFilter: true,
    showModuleFilter: true,
  }
)

// Types
type FilterValue =
  | string
  | number
  | boolean
  | { type: 'date'; from?: string | Date | null; to?: string | Date | null }
  | Array<unknown>
  | null
  | undefined

interface ColFilters {
  [key: string]: FilterValue
}
const qpOptions = {
  transform: {
    get: (v: string | null) => v ?? null,
    set: (v: string | null) => (v === null ? null : v),
  },
}
// Composables
const { t } = useI18n()
const q = useQuasar()
const route = useRoute()
const router = useRouter()
const config = useConfig()
const { hasRole, is: userIs, accessibleModules, submitableModules } = useCurUser()
const { valuationTypeOptions } = useOptionsFormatter()
const timeFilterOptions = getTimeFilterOptions(t)

// Injections
const requestList = inject(requestListKey)!
const {
  onLoad,
  customerRefFilter,
  colFilters: requestListColFilters,
  setColFilters,
  refetch,
  serverSideFilters,
  isUserRequests,
  valReqsFilter,
  getFilteredData,
  isBusy,
  enabled,
} = requestList
const dashboardInstaller = inject(dashboardInstallerKey)!

// Emits
const emit = defineEmits(['loaded'])

// Refs
const showFilters = ref(false)
const expandedRows = ref<string[]>([])
const allRowsExpanded = computed(() => expandedRows.value.length === getFilteredData.value.length)
const tempCustomerRefFilter = ref('')
// Initialize local colFilters from requestList's colFilters (e.g., valuation_type from useDashboardRequestList)
const colFilters: ColFilters = reactive({ ...requestListColFilters.value })
const selectedTimeFilter = ref<string | null>(null)
const timeFilterQP = useRouteQuery('timeFilter', null, qpOptions)

// Helper to calculate date ranges for time filter options
function getDateRange(daysBack: number): { from: string; to: string } {
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - daysBack)

  return {
    from: from.toISOString(),
    to: to.toISOString(),
  }
}

const handleTimeFilterChange = (val: string | null) => {
  timeFilterQP.value = val
}

// Computed - Filters
const filtersCount = computed(() => activeFilters.value.length)

const isActiveFilter = (value: unknown): boolean => {
  if (value == null || value === '') return false

  if (Array.isArray(value)) {
    return value.length > 0
  }

  if (typeof value === 'object' && 'type' in value && (value as { type?: string }).type === 'date') {
    const { from, to } = value as { from?: unknown; to?: unknown }
    return Boolean(from || to)
  }

  return true
}

const activeFilters = computed(() => {
  return Object.entries(colFilters)
    .filter(([, value]) => isActiveFilter(value))
    .map(([key, value]) => {
      const column = dashboardConfig.columns.value.find(
        (col: { filter: string; label: string }) => col.filter === key
      )
      return {
        key,
        label: column?.label,
        value,
      }
    })
})

const toggleExpandAll = () => {
  if (allRowsExpanded.value) {
    // Collapse all rows
    expandedRows.value = []
  } else {
    // Expand all rows
    expandedRows.value = getFilteredData.value.map((row) => row.valuation_request_ref)
  }
}

const showDuplicates = (row: WrappedRequest) => {
  const similarRefs = row.other_data.similar_requests.map((r) => r.valuation_request_ref)
  if (!similarRefs) valReqsFilter.value = null
  const url = router.resolve({
    name: 'dashboard',
    query: { valuation_request_refs: [row.valuation_request_ref, ...similarRefs].join(',') },
  })
  window.open(url.href, '_blank')
}

const dateColor = (thisDate: string | Date) => {
  const difference = utils.dateDiffInBusinessDays(new Date(thisDate), new Date())
  if (difference === 1) return 'text-blue-6 text-bold'
  if (difference >= 2) return 'text-negative text-bold'
  return false
}

const clearFilter = (key: string) => {
  if (key === 'modified_at') {
    timeFilterQP.value = null
  }
  colFilters[key] = null
}

const clearFilters = () => {
  customerRefFilter.value = ''
  tempCustomerRefFilter.value = ''
  // For "regular" users, go back to their own requests when clearing filters
  // Especially important for substitutes when SHOW_ALL_REQUESTS is False
  // For users which by design must see everything (reporter, dispatcher), go back to all requests
  isUserRequests.value = !['reporter', 'dispatcher'].some((role) => hasRole(role))
  if (route.query.valuation_request_refs) router.push({ query: {} })
}

// Auto-redirect for borrowers
if (userIs.value.onlyBorrower) {
  onLoad(() => {
    if (getFilteredData?.value.length === 1) {
      const row = getFilteredData.value[0]
      const shouldRedirectToRequest = !!row.borrower?.is_self && row.is_borrower_submitted
      const name = `valuation.${shouldRedirectToRequest ? 'request' : 'borrower'}`
      switch (row.valuation_type) {
        case enums.modules.DVM:
        case enums.modules.OVM:
          router.replace({
            name,
            query: { valuation_request_ref: row.valuation_request_ref },
          })
          break
      }
    }
  })
}

// Dashboard setup
const dashboard = useBaseDashboard()
dashboardInstaller(dashboard, {
  showDuplicates,
  dateColor,
  requests: requestList,
  curUser: useCurUser(),
  colFilters,
})
const dashboardConfig = dashboard.getConfig()

// Computed - Module filtering
const filteredAccessibleModules = computed(() => {
  return accessibleModules.value.filter((module) =>
    dashboard.modules.value.length
      ? dashboard.modules.value.includes(module)
      : !dashboard.excludedModules.value.includes(module)
  )
})

const isModuleFilterEnabled = computed(
  () => !valReqsFilter.value && filteredAccessibleModules.value.length > 1
)

const isCustomerRefFieldEnabled = computed(
  () =>
    hasRole('dispatcher') ||
    (hasRole('substitute') && config.SUBSTITUTE_FEATURE_ENABLED && !config.SHOW_ALL_REQUESTS)
)

const showValuersWorkload = computed(
  () => props.showValuerWorkload && userIs.value.dvmDispatcher && dashboard.view.valuersWorkflow.value
)

const isAllRequestsTabEnabled = computed(
  () =>
    dashboard.view.allRequestsTab.value &&
    (hasRole('dispatcher') ||
      hasRole('reporter') ||
      (hasRole('substitute') && config.SUBSTITUTE_FEATURE_ENABLED && config.SHOW_ALL_REQUESTS))
)

// Module filter synced with colFilters.valuation_type
const moduleFilter = computed({
  get: () => {
    const filterValue = colFilters.valuation_type
    if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) {
      return filteredAccessibleModules.value
    }
    return Array.isArray(filterValue) ? filterValue : [filterValue]
  },
  set: (val: string[]) => {
    if (!val || val.length === 0) {
      // If nothing selected, reset to all accessible modules
      colFilters.valuation_type = filteredAccessibleModules.value
    } else {
      colFilters.valuation_type = val
    }
  },
})

// Watchers
watch(
  colFilters,
  (newColFilters) => {
    setColFilters(newColFilters)

    if (!newColFilters.modified_at) {
      timeFilterQP.value = null
    }
  },
  { deep: true }
)

watch(
  timeFilterQP,
  (value) => {
    if (!value) {
      selectedTimeFilter.value = null
    }

    const option = timeFilterOptions.find((opt) => opt.value === value)
    if (option && 'days' in option) {
      const dateRange = getDateRange(option.days)
      colFilters.modified_at = {
        type: 'date',
        from: new Date(dateRange.from),
        to: new Date(dateRange.to),
      }
    }
  },
  { immediate: true }
)

// If dashboard has specific module restrictions (e.g., OVM-only dashboard), apply them
if (dashboard.modules.value?.length) {
  serverSideFilters.value.valuation_type = dashboard.modules.value
}

enabled.value = true

dashboard.renderDashboard()

// Route filters
const applyRouteFilters = (to: RouteLocationNormalized) => {
  if (to.query.valuation_request_refs) {
    isUserRequests.value = false
    valReqsFilter.value = to.query.valuation_request_refs as string
  } else valReqsFilter.value = null
}

onMounted(() => {
  applyRouteFilters(route)

  // Initialize module filter from query
  if (route.query.moduleFilter && typeof route.query.moduleFilter === 'string') {
    const modules = route.query.moduleFilter.split(',')
    colFilters.valuation_type = modules
  }
})

onBeforeRouteUpdate((to, _, next) => {
  applyRouteFilters(to)
  next()
})

// Emit loaded when not busy
watch(
  isBusy,
  (value) => {
    if (!value) {
      emit('loaded')
    }
  },
  { immediate: true }
)

defineExpose({
  dashboard,
})
</script>

<style lang="scss">
.request-table {
  border: 1px solid $border;
  border-radius: 4px;

  &--no-border {
    border: none;
  }

  .header {
    font-weight: bold;
    background-color: var(--secondary-bground);
    border-right: 1px solid $border;
  }

  tr.filter-header {
    height: 30px;

    th {
      background-color: $base;
      border-right: 1px solid $border;
      padding: 4px 8px;

      :deep(.q-field__control),
      :deep(.q-field__prepend) {
        height: 30px;
      }

      :deep(.q-field__control) {
        padding: 0 6px;
      }
    }
  }
}
</style>
